import React, { Fragment, useEffect, useState } from "react";
import styles from "./style.module.scss";
import { roles } from "shared/routes/constant";
import { roles as userRoles } from "shared/utils/constants";
import {
  getNewsletterListProductManager,
  deleteNewsLetterProductManager,
} from "shared/services/productManagerService";
import {
  getNewsletterListProjectHead,
  deleteNewsLetterProjectHead,
} from "shared/services/projectHeadService";
import { useSelector } from "react-redux";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import { columnHeadings, actions, headings, skeletonInfo } from "./constants";

import { toastMessage } from "shared/components/toast";
import moment from "moment";
import Confirmation from "shared/modals/confirmation";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Icons } from "assets";
import TableSkeleton from "shared/components/TableSkeleton";
import ItemsPerPage from "shared/components/itemsPerPage";
import NoDataFound from "shared/components/noDataFound";
import Pagination from "shared/components/pagination";
import useDebounce from "shared/customHooks/useDebounce";
import useWindowSize from "shared/customHooks/windowSize";

const NewsLetterSubscriptions = () => {
  const screenWidth = useWindowSize();
  const [totalItems, setTotalItems] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [activePage, setActivePage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [totalPage, setTotalPage] = useState<number>(1);
  // Starts true: getNewsletterList() runs from a useEffect, which fires only
  // after the first render commits. Starting false paints NoDataFound for a
  // frame (the list is still empty) before the skeleton ever appears.
  const [loader, setLoader] = useState<boolean>(true);
  const [newsLetters, setNewsLetters] = useState<any>([]);
  const [newsLetterId, setNewsLetterId] = useState<number | null>(null);
  const [openDeleteNewsletterModal, setOpenDeleteNewsletterModal] =
    useState<boolean>(false);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);

  const {
    user: { user },
  } = useSelector((state: any) => state.root);
  const currentUser = roles.find((role) => role.role_id === Number(user?.role));

  const handleView = (data: any) => {
    setNewsLetterId(data?.id);
    setOpenDeleteNewsletterModal(true);
  };

  const handleDelete = () => {
    let service =
      currentUser?.role_id === userRoles.productManager
        ? deleteNewsLetterProductManager
        : deleteNewsLetterProjectHead;
    setDeleteLoader(true);
    service(newsLetterId)
      .then((data) => {
        toastMessage("success", data?.data?.message);
        setDeleteLoader(false);
        setOpenDeleteNewsletterModal(false);
        getNewsletterList();
      })
      .catch((err) => {
        console.log("err----");
        toastMessage("error", err?.response?.data?.message);
        setDeleteLoader(false);
      });
  };

  const handlers = [
    {
      id: "delete",
      function: handleView,
    },
  ];

  const getNewsletterList = () => {
    let service = null;
    service =
      currentUser?.role_id === userRoles.productManager
        ? getNewsletterListProductManager
        : getNewsletterListProjectHead;
    setLoader(true);
    service(searchText, activePage, itemsPerPage)
      .then(({ data, status }) => {
        if (status) {
          const {
            data: newsletterData,
            current_page,
            total,
            per_page,
            last_page,
          } = data?.data;
          setNewsLetters(newsletterData);
          setTotalPage(last_page);
          setTotalItems(total);
          setCurrentPage(current_page);
          setItemsPerPage(per_page);
          setLoader(false);
        }
      })
      .catch((err: any) => {
        console.log("err--", err);
        setLoader(false);
        toastMessage("error", err?.response?.data?.message);
      });
  };
  useEffect(() => {
    getNewsletterList();
  }, [itemsPerPage, activePage, searchText]);

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      // Nothing changed once trimmed (e.g. only spaces were typed) — keep the
      // current page and skip the request entirely.
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  useBreadcrumbs({
    title: "Newsletter Subscriptions",
    path: routeConstant.newsletterSubscription.path,
  });

  return (
    <div>
      <Box className={styles.topLevelContainer}>
        <Box className={styles.topHeadContainer}>
          <Box>
            <div className={styles.tableHeading}>{headings.mainHeading}</div>
            <div className={styles.tableSubHeading}>{headings.subHeading}</div>
          </Box>
          <Box className={styles.filterContainer}>
            <div className={styles.gridContainer}>
              <Icons.SearchIcon className={styles.searchIcon} />
              <input
                placeholder="Search"
                className={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Box>
        </Box>
        <TableContainer
          className={styles.container}
          component={Paper}
          sx={
            screenWidth > 990
              ? {
                  width: `${screenWidth - 300}px`,
                }
              : {
                  boxShadow: "none",
                }
          }
        >
          <Table className={styles.table} aria-label="simple table">
            <TableHead>
              <TableRow className={styles.tableHead}>
                {columnHeadings?.map((heading, index) => {
                  return <TableCell key={index}>{heading.title}</TableCell>;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loader ? (
                <TableSkeleton
                  rows={skeletonInfo.rows}
                  cellCount={skeletonInfo.cellCount}
                  actionCount={skeletonInfo.actions}
                  showPersonData={false}
                ></TableSkeleton>
              ) : (
                newsLetters?.map((newsletter: any, index: number) => {
                  return (
                    <>
                      <TableRow key={index} className={styles.tableRow}>
                        <TableCell>
                          {newsletter?.email
                            ? newsletter?.email
                            : "Not Provided"}
                        </TableCell>
                        <TableCell>
                          {newsletter?.created_at
                            ? moment(newsletter?.created_at).format(
                                "DD MMM YYYY"
                              )
                            : "Not Provided"}
                        </TableCell>
                        <TableCell>
                          <Box>
                            {actions.map((action: any) => {
                              const handler = handlers.find(
                                (handler) => handler.id === action.id
                              )?.function;
                              return (
                                <button
                                  key={action.id}
                                  className={styles.btnStyles}
                                  onClick={
                                    handler
                                      ? () => handler(newsletter)
                                      : () => {
                                          console.log("Called");
                                        }
                                  }
                                >
                                  <Tooltip title={action.text}>
                                    <span>{action?.icon}</span>
                                  </Tooltip>
                                </button>
                              );
                            })}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          {totalItems &&
          activePage &&
          totalPage &&
          totalPage > 1 &&
          newsLetters.length ? (
            <Pagination
              totalPages={totalPage}
              activePage={activePage}
              setActivePage={setActivePage}
            />
          ) : null}
        </Box>
        <Box>
          {totalItems && activePage && currentPage && newsLetters.length ? (
            <ItemsPerPage
              setActivePage={setActivePage}
              currentPage={currentPage}
              setItemsPerPage={setItemsPerPage}
              itemsPerPage={itemsPerPage ? itemsPerPage : 10}
              totalItems={totalItems}
            />
          ) : null}
        </Box>
        <Box>{!loader && !(newsLetters?.length > 0) && <NoDataFound />}</Box>
      </Box>

      {/* Confirm to Delete NewsLetter */}
      <Confirmation
        open={openDeleteNewsletterModal}
        handleClose={() => setOpenDeleteNewsletterModal(false)}
        loader={deleteLoader}
        text="Are you sure you want to delete this newsletter subscription?"
        showTextArea={false}
        handleClick={handleDelete}
      />
    </div>
  );
};

export default NewsLetterSubscriptions;
